import { pipe } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import { ProjectId } from 'api/models';
import { PageLoading } from 'components/atoms/loading';
import { useGrid } from 'hooks/use-grid';
import { useMemberships } from 'hooks/use-memberships';
import { useTranslate } from 'hooks/use-translation';
import { Memberships } from 'stores/memberships';
import { useCustomerTeamMemberFieldsAsFilterableFields } from 'utils/team-member-fields/customer-team-member-fields';
import { statusField, userEmailIdField } from 'utils/user-fields';
import {
  toColDef,
  transformSelectedMemberData,
} from '../../project-settings/team-members/external-team-member-table/columns';
import {
  TeamMembersListGrid,
  getDefaultOptions,
} from '../../project-settings/team-members/external-team-member-table/list';

type InvitedCustomersListProps = {
  projectId: number;
  userFieldValMap: Record<string, any>;
  setUserFieldValMap: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

export const InvitedCustomersList: React.FC<InvitedCustomersListProps> = ({
  projectId,
  userFieldValMap,
  setUserFieldValMap,
}) => {
  const { fields: teamMemberFields, loading } =
    useCustomerTeamMemberFieldsAsFilterableFields();

  const columnFields = useMemo(() => {
    return loading ? [] : [userEmailIdField, statusField, ...teamMemberFields];
  }, [loading, teamMemberFields]);
  const heightOfGrid = useMemo(() => {
    const numberOfRows = Object.values(userFieldValMap).filter(
      (val) => val.user?.userId == null,
    ).length;
    const heightOfRow = 48;
    const heightOfHeader = 32;

    return Math.min(
      400,
      Math.max(numberOfRows, 5) * heightOfRow + heightOfHeader,
    );
  }, [userFieldValMap]);

  const { gridApi, onGridReady } = useGrid();

  const translate = useTranslate();

  const [, memberships = Memberships.empty()] = useMemberships(
    ProjectId(projectId),
  );

  const withOnUpdate = (mutateAsync) => (columns) => {
    return columns.map((col) => ({
      ...col,
      cellEditorParams: {
        onUpdate: mutateAsync,
      },
    }));
  };

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    const toColumnDefs = pipe(
      toColDef(translate, false, true, memberships),
      withOnUpdate(({ user }, field, from, to) => {
        if (from === to) {
          return;
        }

        const id = user?.userId ?? user?.emailId;

        const keys = Object.keys(userFieldValMap);

        if (keys.includes(id)) {
          setUserFieldValMap((prev) => ({
            ...prev,
            [id]: {
              ...prev[id],
              user: {
                ...prev[id].user,
                fields: [
                  ...(prev[id]?.user?.fields ?? []),
                  { fieldId: field.fieldId, fieldValue: to },
                ],
              },
            },
          }));
        } else {
          setUserFieldValMap((prev) => ({
            ...prev,
            [id]: {
              user: {
                emailId: user.emailId,
                company: { companyId: user.company.companyId },
                fields: [{ fieldId: field.fieldId, fieldValue: to }],
              },
              permissionSet: {
                permissionSetType: user?.permissionSet?.permissionSetType,
              },
            },
          }));
        }
      }),
    );

    const columnDefs = toColumnDefs(columnFields);

    gridApi?.setColumnDefs(columnDefs);

    const value = Object.values(userFieldValMap).filter(
      (val) => val.user?.userId === undefined,
    );

    const rowData = transformSelectedMemberData(value);

    gridApi?.setRowData(rowData);
    gridApi.refreshClientSideRowModel();
  });

  return loading ? (
    <PageLoading />
  ) : (
    <div
      className="ag-theme-balham"
      style={{ height: heightOfGrid, width: '100%', overflow: 'scroll' }}
    >
      <TeamMembersListGrid
        {...getDefaultOptions}
        onGridReady={onGridReady}
        size="large"
      />
    </div>
  );
};
